import toNumber from 'lodash/toNumber'
import React, { useState, useEffect } from 'react'
import { Stack } from 'react-bootstrap'
import { InlineDisplayContent, NumberInput, TextAreaInput, TextInput } from '@ztechapps/webui.input'
import { generateSerialNumbers } from '@ztechapps/common.utils'
import type { InventoryBySerialNumber } from '@ztechapps/common.contract'
import type { SerialNumberStrategy, SerialNumberInputProps, ValidationResult } from './types'

const MAX_PATTERN_GENERATION_LIMIT = 1000

export const validatePatternGeneration = (input: string, maxQuantity: number): ValidationResult => {
  const [pattern, count] = input.split('|')

  if (!pattern?.trim()) {
    return { isValid: true, serialNumbers: [] }
  }

  try {
    // Determine the count to use for generation
    let countToUse: number
    if (maxQuantity === -1) {
      const userCount = toNumber(count || '0')
      if (isNaN(userCount) || userCount < 1) {
        return {
          isValid: false,
          error: `Please enter a valid quantity (1-${MAX_PATTERN_GENERATION_LIMIT})`,
          serialNumbers: [],
        }
      }
      if (userCount > MAX_PATTERN_GENERATION_LIMIT) {
        return {
          isValid: false,
          error: `Maximum quantity for pattern generation is ${MAX_PATTERN_GENERATION_LIMIT}`,
          serialNumbers: [],
        }
      }
      countToUse = userCount
    } else {
      countToUse = maxQuantity
    }

    const codes = generateSerialNumbers(pattern, countToUse)

    // Check for duplicates
    const uniqueCodes = new Set(codes)
    if (uniqueCodes.size !== codes.length) {
      return {
        isValid: false,
        error: 'Pattern generates duplicate serial numbers',
        serialNumbers: [],
      }
    }

    const serialNumbers = codes.map((code) => ({ code }))
    return { isValid: true, serialNumbers }
  } catch (err) {
    return {
      isValid: false,
      error: err instanceof Error ? err.message : 'Invalid pattern',
      serialNumbers: [],
    }
  }
}

export const getPatternInitialValue = (_serialNumbers: InventoryBySerialNumber[]): string => {
  // Pattern mode doesn't restore from existing serial numbers
  return ''
}

export const PatternGeneratorInput = ({ value, onChange, disabled, maxQuantity, error }: SerialNumberInputProps) => {
  const [pattern, count] = value.split('|')
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([])
  // const maxQtyDisplay = maxQuantity === -1 ? 'unlimited' : maxQuantity

  // Generate preview whenever pattern or count changes
  useEffect(() => {
    if (!pattern?.trim()) {
      setGeneratedCodes([])
      return
    }

    try {
      const countToUse = maxQuantity === -1 ? toNumber(count || '0') : maxQuantity
      if (countToUse > 0 && countToUse <= MAX_PATTERN_GENERATION_LIMIT) {
        const codes = generateSerialNumbers(pattern, countToUse)
        setGeneratedCodes(codes)
      } else {
        setGeneratedCodes([])
      }
    } catch (err) {
      console.warn('Pattern generation preview failed:', err)
      setGeneratedCodes([])
    }
  }, [pattern, count, maxQuantity])

  const handlePatternChange = (newPattern: string) => {
    onChange(`${newPattern}|${count || ''}`)
  }

  const handleCountChange = (newCount: string) => {
    onChange(`${pattern || ''}|${newCount}`)
  }

  return (
    <Stack gap={2}>
      <InlineDisplayContent
        label="Pattern"
        help="Use X for incrementing digits, 0 for padding. Example: PMX_00XX generates PMX_0001, PMX_0002, etc. Use \X for literal X character."
        showHelpAsInline
      >
        <TextInput
          value={pattern || ''}
          onChange={(e) => handlePatternChange(e.target.value)}
          placeholder="e.g., PMX_00XX or ABC-XXX"
          disabled={disabled}
        />
      </InlineDisplayContent>

      <InlineDisplayContent
        label="Required Quantity"
        showHelpAsInline
        help={
          maxQuantity === -1
            ? `Enter the number of serial numbers to generate (1-${MAX_PATTERN_GENERATION_LIMIT})`
            : 'Number of serial numbers to generate'
        }
      >
        <NumberInput
          value={maxQuantity === -1 ? count || '' : maxQuantity?.toString() || '0'}
          onChange={(e) => handleCountChange(e.target.value)}
          disabled={maxQuantity !== -1 || disabled}
          placeholder={maxQuantity === -1 ? `Enter quantity (1-${MAX_PATTERN_GENERATION_LIMIT})` : ''}
          min={1}
          max={MAX_PATTERN_GENERATION_LIMIT}
        />
      </InlineDisplayContent>

      {/* Preview */}
      <div>
        <div className="small fw-bold mb-1">Preview ({generatedCodes.length} serial numbers):</div>

        <TextAreaInput
          rows={Math.min(generatedCodes.length || 1, 7)}
          value={generatedCodes.join('\n')}
          className="font-monospace"
          readOnly={true}
          disabled={true}
        />
      </div>

      {error && <div className="text-danger small">{error}</div>}
    </Stack>
  )
}

export const PatternStrategy: SerialNumberStrategy = {
  validator: validatePatternGeneration,
  getInitialValue: getPatternInitialValue,
  InputComponent: PatternGeneratorInput,
}
